import {
  BooleanValidation,
  ConfigOptionValidation,
  StringValidation,
} from '../../../utils/component-validations';
import { reads } from 'macro-decorators';
import { tracked } from '@glimmer/tracking';
import Container, {
  FLEX_AND_GRID_PROPS,
  FLEX_ONLY_PROPS,
  GRID_CONVENIENCE_PROPS,
  GRID_ONLY_PROPS,
} from './container.gjs';
import SpdsAutofocus from '../../../modifiers/spds-autofocus';
import Super from './base.gjs';
import styles from './button.module.scss';

export const ALIGN_ITEMS_DEFAULT = 'center';
export const ELEMENT_TAG_DEFAULT = 'button';
export const GAP_DEFAULT = '8px';
export const JUSTIFY_CONTENT_DEFAULT = 'center';
export const TEXT_ALIGN_DEFAULT = 'center';

/**
 * Button component for SPDS
 *
 * Arguments
 * @argument {boolean} absolute position the button absolutely
 * @argument {boolean} autofocus automatically focus the button on render
 * @argument {boolean} column arrange content in a column
 * @argument {boolean} disabled disable the button
 * @argument {boolean} discardClick prevent click events from bubbling
 * @argument {boolean} error apply error styling
 * @argument {boolean} fitContent size to fit content
 * @argument {boolean} fitContentHeight height sized to fit content
 * @argument {boolean} fitContentWidth width sized to fit content
 * @argument {boolean} fixed apply fixed positioning
 * @argument {boolean} grid use grid layout (instead of flexbox)
 * @argument {boolean} iconOnly button only contains an icon
 * @argument {boolean} inline display button inline
 * @argument {boolean} inputContainer apply input container styling
 * @argument {boolean} reverse reverse flex direction
 * @argument {boolean} subgrid use subgrid for grid template
 * @argument {string} alignContent CSS align-content property
 * @argument {string} alignSelf CSS align-self property
 * @argument {string} area convenience property for gridArea
 * @argument {string} areas convenience property for gridTemplateAreas
 * @argument {string} autoFlow convenience property for gridAutoFlow
 * @argument {string} columns convenience property for gridTemplateColumns
 * @argument {string} flex CSS flex property
 * @argument {string} flexWrap CSS flex-wrap property
 * @argument {string} gridArea CSS grid-area property
 * @argument {string} gridAutoColumns CSS grid-auto-columns property
 * @argument {string} gridAutoFlow CSS grid-auto-flow property
 * @argument {string} gridAutoRows CSS grid-auto-rows property
 * @argument {string} gridColumn CSS grid-column property
 * @argument {string} gridRow CSS grid-row property
 * @argument {string} gridTemplateAreas CSS grid-template-areas property
 * @argument {string} gridTemplateColumns CSS grid-template-columns property
 * @argument {string} gridTemplateRows CSS grid-template-rows property
 * @argument {string} justifyItems CSS justify-items property
 * @argument {string} justifySelf CSS justify-self property
 * @argument {string} order CSS order property
 * @argument {string} rows convenience property for gridTemplateRows
 * @argument {string} size button size, options: 'normal', 'small'
 * @argument {string} tag HTML tag to use for rendering, defaults to 'button'
 * @argument {string} text text content for the button
 * @argument {string} type button type attribute, defaults to 'button'
 */
export default class SpdsCoreButton extends Super {
  arguments = [
    new BooleanValidation({
      optional: [
        'absolute',
        'autofocus',
        'column',
        'disabled',
        'error',
        'fitContent',
        'fitContentHeight',
        'fitContentWidth',
        'fixed',
        'grid',
        'iconOnly',
        'inline',
        'reverse',
        'subgrid',
      ],
    }),
    new ConfigOptionValidation({ optional: [{ name: 'size', options: ['normal', 'small'] }] }),
    new StringValidation({
      optional: [
        ...FLEX_AND_GRID_PROPS,
        ...FLEX_ONLY_PROPS,
        ...GRID_CONVENIENCE_PROPS,
        ...GRID_ONLY_PROPS,
        'tag',
        'text',
        'type',
      ],
    }),
  ];
  baseClass = 'spds-button';
  classesToMapToArgs = ['iconOnly', 'size'];
  classesToMapToAttrs = ['hasBorder'];
  componentName = 'Spds::Core::Button';
  styles = styles;

  @tracked baseElement;

  @reads('args.tag', ELEMENT_TAG_DEFAULT) tag;
  @reads('args.type', ELEMENT_TAG_DEFAULT) type;

  get alignItems() {
    return this.args.alignItems ?? ALIGN_ITEMS_DEFAULT;
  }

  get gap() {
    return this.args.gap ?? GAP_DEFAULT;
  }

  get justifyContent() {
    return this.args.justifyContent ?? JUSTIFY_CONTENT_DEFAULT;
  }

  /**
   * determines if role attribute should be set
   * @return {string|undefined} button role attribute value when not a button element
   */
  get role() {
    return this.tag === ELEMENT_TAG_DEFAULT ? undefined : ELEMENT_TAG_DEFAULT;
  }

  /**
   * determines the variant style of the button for data-test attributes
   * @return {string} button variant value
   */
  get variant() {
    if (this.link) return 'link';
    if (this.variantClass) return this.variantClass.split('-').at(-1);

    return this.tag;
  }

  <template>
    <Container
      @absolute={{@absolute}}
      @alignContent={{@alignContent}}
      @alignItems={{this.alignItems}}
      @alignSelf={{@alignSelf}}
      @area={{@area}}
      @areas={{@areas}}
      @autoFlow={{@autoFlow}}
      @column={{@column}}
      @columns={{@columns}}
      @content={{@text}}
      @error={{@error}}
      @fitContent={{@fitContent}}
      @fitContentHeight={{@fitContentHeight}}
      @fitContentWidth={{@fitContentWidth}}
      @fixed={{@fixed}}
      @flex={{@flex}}
      @flexWrap={{@flexWrap}}
      @gap={{this.gap}}
      @grid={{@grid}}
      @gridArea={{@gridArea}}
      @gridAutoColumns={{@gridAutoColumns}}
      @gridAutoFlow={{@gridAutoFlow}}
      @gridAutoRows={{@gridAutoRows}}
      @gridColumn={{@gridColumn}}
      @gridRow={{@gridRow}}
      @gridTemplateAreas={{@gridTemplateAreas}}
      @gridTemplateColumns={{@gridTemplateColumns}}
      @gridTemplateRows={{@gridTemplateRows}}
      @inline={{@inline}}
      @justifyContent={{this.justifyContent}}
      @justifyItems={{@justifyItems}}
      @justifySelf={{@justifySelf}}
      @order={{@order}}
      @reverse={{@reverse}}
      @rows={{@rows}}
      @subgrid={{@subgrid}}
      @tag={{this.tag}}
      class={{this.classes}}
      data-test-spds-button={{this.variant}}
      disabled={{@disabled}}
      role={{this.role}}
      type={{this.type}}
      ...attributes
      {{SpdsAutofocus @autofocus}}
      {{this.setupComponent}}
      {{this.validateArguments}}
    >
      {{yield}}
    </Container>
  </template>
}
